import {
  type ComparisonOperator,
  type PreviewVariable,
  PreviewVariableCollector,
  compareValues,
  lookupValue,
  type NameMatching,
  mirrorOperator,
  toNumber,
  toText,
  type PersonalizationPrimitive,
  type RenderErrorHandler,
  truthy,
  unescapeSampleValue,
} from "./preview-variables";

// A preview interpreter for the Sailthru Zephyr subset marketing emails use:
// `{expr}` output, `{if}`/`{elseif}`/`{else}`/`{/if}`, `{foreach}`, assignment,
// ternaries and the common string and number functions. Like the AMPscript
// interpreter it never throws: a brace that doesn't parse as Zephyr (CSS, for
// example) is emitted unchanged, and unknown functions render as empty.

type Values = Record<string, PersonalizationPrimitive>;

// Sailthru: "Zephyr variables are case sensitive."
const ZEPHYR_NAMES: NameMatching = { ignoreCase: false };

// The renderer pins `now` to the send time, as it does for Liquid and AMPscript.
const SEND_TIME_VARIABLE = "now";

type Token =
  | { type: "string"; value: string }
  | { type: "number"; value: number }
  | { type: "ident"; value: string }
  | { type: "op"; value: string };

type Expr =
  | { kind: "literal"; value: PersonalizationPrimitive | null }
  | { kind: "path"; segments: string[] }
  | { kind: "call"; name: string; args: Expr[] }
  | { kind: "unary"; op: string; operand: Expr }
  | { kind: "binary"; op: string; left: Expr; right: Expr }
  | { kind: "ternary"; condition: Expr; then: Expr; otherwise: Expr };

type Instruction =
  | { kind: "text"; text: string }
  | { kind: "output"; expr: Expr }
  | { kind: "assign"; name: string; expr: Expr }
  // A null condition or list did not parse; its block stays closed so nesting balances.
  | { kind: "if" | "elseif"; condition: Expr | null }
  | { kind: "else" | "endif" | "endforeach" }
  | { kind: "foreach"; list: Expr | null; item: string | null };

interface Value {
  raw: unknown;
  sources: Set<string>;
  direct?: string;
  /** For a loop item, the list attribute its fields are read from. */
  itemOf?: string;
}

const COMPARISON_OPERATORS: Record<string, ComparisonOperator> = {
  "==": "==",
  "!=": "!=",
  ">": ">",
  "<": "<",
  ">=": ">=",
  "<=": "<=",
};

const BINARY_PRECEDENCE: Record<string, number> = {
  "||": 2,
  or: 2,
  "&&": 3,
  and: 3,
  "==": 4,
  "!=": 4,
  ">": 5,
  "<": 5,
  ">=": 5,
  "<=": 5,
  "+": 6,
  "-": 6,
  "*": 7,
  "/": 7,
  "%": 7,
};

// `{` directly followed by a non-space, non-brace character opens a tag, which
// keeps most CSS intact; `{{expr}}` is the double-brace form of the same output.
const TAG_BODY = `((?:"[^"]*"|'[^']*'|[^{}"'])*)`;
const TAG_RE = new RegExp(
  `\\{\\*[\\s\\S]*?\\*\\}|\\{\\{\\s*${TAG_BODY}\\s*\\}\\}|\\{(?![\\s{])${TAG_BODY}\\}`,
  "g",
);

/** True when the template has a Zephyr control tag or a profile reference. */
export function containsZephyr(template: string): boolean {
  return /\{(?:if |elseif |else if |else\}|\/if\}|foreach |\/foreach\}|profile\.)/.test(
    template,
  );
}

/** True when some brace in the template parses as a Zephyr tag rather than CSS or text. */
export function containsZephyrTags(template: string): boolean {
  return parseTemplate(template).some(
    (instruction) => instruction.kind !== "text",
  );
}

export function renderZephyr(
  template: string,
  values: Values,
  onError?: RenderErrorHandler,
): string {
  return new Interpreter("render", values, undefined, onError).run(
    parseTemplate(template),
  );
}

export function extractZephyrVariables(template: string): PreviewVariable[] {
  const collector = new PreviewVariableCollector(ZEPHYR_NAMES);
  new Interpreter("trace", {}, collector).run(parseTemplate(template));
  return collector.list();
}

function parseTemplate(template: string): Instruction[] {
  const instructions: Instruction[] = [];
  let last = 0;
  const pushText = (text: string) => {
    if (text) instructions.push({ kind: "text", text });
  };
  for (const match of template.matchAll(TAG_RE)) {
    const index = match.index ?? 0;
    pushText(template.slice(last, index));
    last = index + match[0].length;
    if (match[0].startsWith("{*")) continue;
    const instruction = parseTag((match[1] ?? match[2] ?? "").trim());
    if (instruction) instructions.push(instruction);
    else pushText(match[0]);
  }
  pushText(template.slice(last));
  return instructions;
}

function parseTag(body: string): Instruction | null {
  if (body === "else") return { kind: "else" };
  if (body === "/if") return { kind: "endif" };
  if (body === "/foreach") return { kind: "endforeach" };
  const keyword = /^(if|elseif|else\s+if|foreach)\s+([\s\S]+)$/.exec(body);
  if (keyword?.[1] === "if") {
    return { kind: "if", condition: parseExpression(keyword[2] ?? "") };
  }
  if (keyword?.[1] === "elseif" || keyword?.[1]?.startsWith("else")) {
    return { kind: "elseif", condition: parseExpression(keyword[2] ?? "") };
  }
  if (keyword?.[1] === "foreach") {
    const loop =
      /^([\s\S]+?)\s+as\s+(?:[A-Za-z_]\w*\s*,\s*)?([A-Za-z_]\w*)$/.exec(
        keyword[2] ?? "",
      );
    return {
      kind: "foreach",
      list: loop ? parseExpression(loop[1] ?? "") : null,
      item: loop?.[2] ?? null,
    };
  }
  const assignment = /^([A-Za-z_]\w*)\s*=(?!=)([\s\S]+)$/.exec(body);
  if (assignment?.[1]) {
    const expr = parseExpression(assignment[2] ?? "");
    return expr ? { kind: "assign", name: assignment[1], expr } : null;
  }
  const expr = parseExpression(body);
  return expr ? { kind: "output", expr } : null;
}

function parseExpression(source: string): Expr | null {
  const tokens = lex(source);
  if (!tokens) return null;
  const parser = new Parser(tokens);
  try {
    const expr = parser.expression();
    return parser.done() ? expr : null;
  } catch (error) {
    if (error instanceof ParseError) return null;
    throw error;
  }
}

function lex(source: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const char = source[i] ?? "";
    if (/\s/.test(char)) {
      i++;
    } else if (char === '"' || char === "'") {
      const end = source.indexOf(char, i + 1);
      if (end === -1) return null;
      tokens.push({ type: "string", value: source.slice(i + 1, end) });
      i = end + 1;
    } else if (/[0-9]/.test(char)) {
      const text = /^\d+(?:\.\d+)?/.exec(source.slice(i))?.[0] ?? char;
      tokens.push({ type: "number", value: Number(text) });
      i += text.length;
    } else if (/[A-Za-z_$]/.test(char)) {
      const text =
        /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(source.slice(i))?.[0] ?? char;
      tokens.push({ type: "ident", value: text });
      i += text.length;
    } else {
      const op = ["==", "!=", ">=", "<=", "&&", "||"].find((candidate) =>
        source.startsWith(candidate, i),
      );
      const value = op ?? char;
      if (!op && !"+-*/%<>!?:().,[]".includes(char)) return null;
      tokens.push({ type: "op", value });
      i += value.length;
    }
  }
  return tokens;
}

class ParseError extends Error {}

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  done(): boolean {
    return this.pos === this.tokens.length;
  }

  expression(): Expr {
    const condition = this.binary(0);
    if (this.isOp("?")) {
      this.pos++;
      const then = this.expression();
      this.expectOp(":");
      const otherwise = this.expression();
      return { kind: "ternary", condition, then, otherwise };
    }
    return condition;
  }

  private binary(minPrecedence: number): Expr {
    let left = this.unary();
    for (;;) {
      const token = this.peek();
      const op =
        token?.type === "op" || token?.type === "ident"
          ? token.value.toLowerCase()
          : "";
      const precedence = BINARY_PRECEDENCE[op];
      if (precedence === undefined || precedence <= minPrecedence) return left;
      this.pos++;
      left = { kind: "binary", op, left, right: this.binary(precedence) };
    }
  }

  private unary(): Expr {
    const token = this.peek();
    if (
      (token?.type === "op" && (token.value === "!" || token.value === "-")) ||
      (token?.type === "ident" && token.value.toLowerCase() === "not")
    ) {
      this.pos++;
      return {
        kind: "unary",
        op: token.value === "-" ? "-" : "!",
        operand: this.unary(),
      };
    }
    return this.postfix();
  }

  private postfix(): Expr {
    const token = this.peek();
    if (!token) throw new ParseError();
    this.pos++;
    if (token.type === "string" || token.type === "number") {
      return { kind: "literal", value: token.value };
    }
    if (token.type === "op" && token.value === "(") {
      const inner = this.expression();
      this.expectOp(")");
      return inner;
    }
    if (token.type !== "ident") throw new ParseError();
    const word = token.value.toLowerCase();
    if (word === "true" || word === "false") {
      return { kind: "literal", value: word === "true" };
    }
    if (word === "null") return { kind: "literal", value: null };
    if (this.isOp("(")) {
      this.pos++;
      const args: Expr[] = [];
      while (!this.isOp(")")) {
        args.push(this.expression());
        if (this.isOp(",")) this.pos++;
        else break;
      }
      this.expectOp(")");
      return { kind: "call", name: word, args };
    }
    const segments = [token.value];
    for (;;) {
      if (this.isOp(".")) {
        this.pos++;
        const next = this.peek();
        if (next?.type !== "ident" && next?.type !== "number") {
          throw new ParseError();
        }
        this.pos++;
        segments.push(String(next.value));
      } else if (this.isOp("[")) {
        this.pos++;
        const key = this.peek();
        if (key?.type !== "string" && key?.type !== "number") {
          throw new ParseError();
        }
        this.pos++;
        this.expectOp("]");
        segments.push(String(key.value));
      } else {
        return { kind: "path", segments };
      }
    }
  }

  private isOp(value: string): boolean {
    const token = this.peek();
    return token?.type === "op" && token.value === value;
  }

  private expectOp(value: string): void {
    if (!this.isOp(value)) throw new ParseError();
    this.pos++;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
}

interface Frame {
  parentActive: boolean;
  active: boolean;
  taken: boolean;
  loopItem?: string;
}

class Interpreter {
  private readonly locals = new Map<string, Value>();
  private readonly frames: Frame[] = [];
  private inCondition = false;
  private output = "";

  constructor(
    private readonly mode: "render" | "trace",
    private readonly values: Values,
    private readonly collector?: PreviewVariableCollector,
    private readonly onError?: RenderErrorHandler,
  ) {}

  run(instructions: Instruction[]): string {
    for (const instruction of instructions) this.step(instruction);
    return this.output;
  }

  private get active(): boolean {
    return this.frames.at(-1)?.active ?? true;
  }

  private step(instruction: Instruction): void {
    switch (instruction.kind) {
      case "if": {
        const parentActive = this.active;
        const taken = parentActive && this.condition(instruction.condition);
        this.frames.push({
          parentActive,
          active: this.mode === "trace" ? parentActive : taken,
          taken,
        });
        return;
      }
      case "elseif": {
        const frame = this.frames.at(-1);
        if (!frame) return;
        // Evaluated even after an earlier branch was taken, so tracing reads every branch's variables.
        const holds =
          frame.parentActive && this.condition(instruction.condition);
        const taken = !frame.taken && holds;
        frame.active = this.mode === "trace" ? frame.parentActive : taken;
        frame.taken ||= taken;
        return;
      }
      case "else": {
        const frame = this.frames.at(-1);
        if (!frame) return;
        frame.active =
          this.mode === "trace"
            ? frame.parentActive
            : frame.parentActive && !frame.taken;
        return;
      }
      case "endif":
        this.frames.pop();
        return;
      case "foreach": {
        // A preview shows one pass, with the item's fields read from the list.
        const parentActive = this.active;
        if (!instruction.list || !instruction.item) {
          this.frames.push({ parentActive, active: false, taken: true });
          return;
        }
        const list = parentActive
          ? this.evaluate(instruction.list)
          : { raw: null, sources: new Set<string>() };
        this.locals.set(instruction.item, {
          raw: null,
          sources: list.sources,
          itemOf: list.direct,
        });
        this.frames.push({
          parentActive,
          active: parentActive,
          taken: true,
          loopItem: instruction.item,
        });
        return;
      }
      case "endforeach": {
        const frame = this.frames.pop();
        if (frame?.loopItem) this.locals.delete(frame.loopItem);
        return;
      }
      default:
        break;
    }
    if (!this.active) return;
    switch (instruction.kind) {
      case "text":
        this.output += instruction.text;
        return;
      case "output":
        this.output += toText(this.evaluate(instruction.expr).raw);
        return;
      case "assign":
        this.locals.set(instruction.name, this.evaluate(instruction.expr));
        return;
    }
  }

  private condition(expr: Expr | null): boolean {
    if (!expr) return false;
    this.inCondition = true;
    const value = this.evaluate(expr);
    this.inCondition = false;
    for (const source of value.sources) this.collector?.read(source, true);
    return truthy(value.raw);
  }

  private evaluate(expr: Expr): Value {
    switch (expr.kind) {
      case "literal":
        return { raw: expr.value, sources: new Set() };
      case "path":
        return this.path(expr.segments);
      case "unary": {
        const operand = this.evaluate(expr.operand);
        return {
          raw: expr.op === "-" ? -toNumber(operand.raw) : !truthy(operand.raw),
          sources: operand.sources,
        };
      }
      case "ternary": {
        const condition = this.evaluate(expr.condition);
        if (this.mode === "trace") {
          for (const source of condition.sources) {
            this.collector?.read(source, true);
          }
          this.evaluate(expr.then);
          this.evaluate(expr.otherwise);
        }
        return truthy(condition.raw)
          ? this.evaluate(expr.then)
          : this.evaluate(expr.otherwise);
      }
      case "binary":
        return this.binary(expr.op, expr.left, expr.right);
      case "call":
        return this.call(
          expr.name,
          expr.args.map((arg) => this.evaluate(arg)),
        );
    }
  }

  private sendTime(): Date {
    const pinned = lookupValue(this.values, SEND_TIME_VARIABLE, ZEPHYR_NAMES);
    const date = new Date(typeof pinned === "string" ? pinned : Date.now());
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }

  private path(segments: string[]): Value {
    const [root = "", ...rest] = segments;
    const local = this.locals.get(root);
    if (local) {
      if (rest.length === 0) return local;
      if (local.itemOf)
        return this.attribute([local.itemOf, ...rest].join("."));
      return { raw: null, sources: local.sources };
    }
    return this.attribute(segments.join("."));
  }

  private attribute(name: string): Value {
    this.collector?.read(name, this.inCondition);
    return {
      raw: lookupValue(this.values, name, ZEPHYR_NAMES) ?? null,
      sources: new Set([name]),
      direct: name,
    };
  }

  private binary(op: string, leftExpr: Expr, rightExpr: Expr): Value {
    const left = this.evaluate(leftExpr);
    const right = this.evaluate(rightExpr);
    const sources = new Set([...left.sources, ...right.sources]);
    switch (op) {
      case "&&":
      case "and":
        return { raw: truthy(left.raw) && truthy(right.raw), sources };
      case "||":
      case "or":
        return { raw: truthy(left.raw) ? left.raw : right.raw, sources };
      case "+":
        return {
          raw:
            typeof left.raw === "number" && typeof right.raw === "number"
              ? left.raw + right.raw
              : toText(left.raw) + toText(right.raw),
          sources,
        };
      case "-":
        return { raw: toNumber(left.raw) - toNumber(right.raw), sources };
      case "*":
        return { raw: toNumber(left.raw) * toNumber(right.raw), sources };
      case "/":
        return {
          raw:
            toNumber(right.raw) === 0
              ? 0
              : toNumber(left.raw) / toNumber(right.raw),
          sources,
        };
      case "%":
        return {
          raw:
            toNumber(right.raw) === 0
              ? 0
              : toNumber(left.raw) % toNumber(right.raw),
          sources,
        };
    }
    const operator = COMPARISON_OPERATORS[op];
    if (!operator) return { raw: null, sources };
    if (this.inCondition) {
      this.recordComparison(left, right, operator);
      this.recordComparison(right, left, mirrorOperator(operator));
    }
    return {
      raw: compareValues(
        unescapeSampleValue(left.raw),
        unescapeSampleValue(right.raw),
        operator,
        { ignoreCase: false },
      ),
      sources,
    };
  }

  private recordComparison(
    subject: Value,
    other: Value,
    operator: ComparisonOperator,
  ): void {
    if (!subject.direct || other.sources.size > 0) return;
    const literal = other.raw;
    if (
      typeof literal === "string" ||
      typeof literal === "number" ||
      typeof literal === "boolean"
    ) {
      this.collector?.compare(subject.direct, operator, literal);
    }
  }

  private call(name: string, args: Value[]): Value {
    const sources = new Set(args.flatMap((arg) => [...arg.sources]));
    const [first, second, third] = args;
    const text = toText(first?.raw);
    const passThrough = (raw: unknown): Value => ({
      raw,
      sources,
      direct: first?.direct,
    });
    switch (name) {
      case "upper":
      case "uppercase":
        return passThrough(text.toUpperCase());
      case "lower":
      case "lowercase":
        return passThrough(text.toLowerCase());
      case "ucfirst":
        return passThrough(text.charAt(0).toUpperCase() + text.slice(1));
      case "ucwords":
        return passThrough(text.replace(/\b\w/g, (c) => c.toUpperCase()));
      case "trim":
        return passThrough(text.trim());
      case "length":
      case "strlen":
      case "count":
        return { raw: text.length, sources };
      case "number": {
        // toLocaleString throws outside 0-100; 20 is plenty for a preview.
        const decimals = Math.min(
          20,
          Math.max(0, Math.trunc(second ? toNumber(second.raw) : 0)),
        );
        return {
          raw: toNumber(first?.raw).toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }),
          sources,
        };
      }
      case "replace":
        return {
          raw: toText(third?.raw).split(text).join(toText(second?.raw)),
          sources,
        };
      case "contains":
        return { raw: text.includes(toText(second?.raw)), sources };
      case "substr": {
        const start = toNumber(second?.raw);
        const length = third ? toNumber(third.raw) : undefined;
        return {
          raw: text.slice(
            start,
            length === undefined ? undefined : start + length,
          ),
          sources,
        };
      }
      case "time":
        return {
          raw: Math.floor(this.sendTime().getTime() / 1000),
          sources,
        };
      case "date": {
        const timestamp = second ? toNumber(second.raw) : null;
        const date =
          timestamp === null ? this.sendTime() : new Date(timestamp * 1000);
        return { raw: formatZephyrDate(date, text), sources };
      }
      case "isset":
        return { raw: first?.raw != null && first.raw !== "", sources };
      default:
        // Unsupported functions (content selection, HTTP, hashing) render as empty.
        this.onError?.(`Unsupported preview function: ${name}`);
        return { raw: "", sources };
    }
  }
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Zephyr's date() takes Java SimpleDateFormat patterns, read here in UTC like the pinned send time.
const DATE_PATTERN_RE =
  /y{2,4}|M{1,4}|d{1,2}|E{3,4}|H{1,2}|h{1,2}|m{2}|s{2}|a|'[^']*'/g;

function formatZephyrDate(date: Date, pattern: string): string {
  if (!pattern) return date.toLocaleDateString("en-US", { timeZone: "UTC" });
  const pad = (value: number) => String(value).padStart(2, "0");
  const hours = date.getUTCHours();
  return pattern.replace(DATE_PATTERN_RE, (token) => {
    switch (token) {
      case "yyyy":
      case "yyy":
        return String(date.getUTCFullYear());
      case "yy":
        return String(date.getUTCFullYear()).slice(-2);
      case "MMMM":
        return MONTHS[date.getUTCMonth()] ?? "";
      case "MMM":
        return (MONTHS[date.getUTCMonth()] ?? "").slice(0, 3);
      case "MM":
        return pad(date.getUTCMonth() + 1);
      case "M":
        return String(date.getUTCMonth() + 1);
      case "dd":
        return pad(date.getUTCDate());
      case "d":
        return String(date.getUTCDate());
      case "EEEE":
        return WEEKDAYS[date.getUTCDay()] ?? "";
      case "EEE":
        return (WEEKDAYS[date.getUTCDay()] ?? "").slice(0, 3);
      case "HH":
        return pad(hours);
      case "H":
        return String(hours);
      case "hh":
        return pad(((hours + 11) % 12) + 1);
      case "h":
        return String(((hours + 11) % 12) + 1);
      case "mm":
        return pad(date.getUTCMinutes());
      case "ss":
        return pad(date.getUTCSeconds());
      case "a":
        return hours >= 12 ? "PM" : "AM";
      default:
        return token.slice(1, -1);
    }
  });
}
